import VideoList from "./VideoList";
import {useContext, useEffect, useRef, useState} from "react";
import AppContext from "../context/AppContext";
import VideoPlayer from "./video-player/VideoPlayer";
import TagList from "./TagList";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFolderOpen} from "@fortawesome/free-solid-svg-icons";

import './VideoLayer.scss'
import {Link} from "react-router-dom";
import {getVideoLink} from "../lib/static";
import {useApi} from "../api";
import QueuePanel from "./QueuePanel";

let layer: HTMLDivElement|null = null

export default () => {
    const api = useApi()
    const {
        dirLoading, selectedVideo, setSelectedVideo, videoLoop, setVideoLoop,
        videoAutoPlay, setVideoAutoPlay, files, nextVideo, prevVideo,
        queue, queueIndex, setQueue, updateQueue, clearQueue,
    } = useContext(AppContext)

    const containerRef = useRef<HTMLDivElement>(null)
    const [ fullScreen, setFullScreen ] = useState<boolean>(false)
    const [ sidebarTab, setSidebarTab ] = useState<'files' | 'queue'>('files')

    useEffect(() => {
        if (queue.length > 0) setSidebarTab('queue')
        else setSidebarTab('files')
    }, [queue.length])
    const [ nextRequested, setNextRequested ] = useState<boolean>(false)
    const [ hasHover, setHasHover ] = useState<boolean>(false)
    const [ lastHoverTimeout, setLastHoverTimeout ] = useState<number | null>(null)
    const [ ctrlPressed, setCtrlPressed ] = useState<boolean>(false)
    const [ scrollPosition, setScrollPosition ] = useState<number>(0)

    const ctrlRef = useRef<boolean>(false)
    ctrlRef.current = ctrlPressed

    const nextVideoRef = useRef(nextVideo)
    nextVideoRef.current = nextVideo

    const prevVideoRef = useRef(prevVideo)
    prevVideoRef.current = prevVideo

    const onLayerOpen = (el: HTMLDivElement) => {
        layer = el
    }

    const onSmallListLoaded = () => {
        if (layer) {
            setTimeout(() => {
                layer?.querySelector('.file-list')?.querySelector('.file-preview.selected')?.scrollIntoView()
            }, 0)
        }
    }

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Control') setCtrlPressed(true)
        }
        const onKeyUp = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Control': setCtrlPressed(false); break
                case 'ArrowLeft':
                    if (!ctrlRef.current) break
                    startPrev(e); break
                case 'ArrowRight':
                    if (!ctrlRef.current) break
                    startNext(e); break
                case 'Escape':
                    setSelectedVideo(null)
                    window.history.pushState(null, '', window.location.href.split('?')[0])
                    break
            }
        }
        const onBlur = () => setCtrlPressed(false)

        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('keyup', onKeyUp)
        window.addEventListener('blur', onBlur)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('keyup', onKeyUp)
            window.removeEventListener('blur', onBlur)
        }
    }, []);

    useEffect(() => { onSmallListLoaded() }, [ selectedVideo ])

    useEffect(() => {
        if (nextRequested) {
            setNextRequested(false)
            startNext()
        }
    }, [ nextRequested ])

    useEffect(() => {
        if (selectedVideo?.fileType === 'photo' && videoAutoPlay) {
            setTimeout(() => setNextRequested(true), 5000)
        }
        if (videoAutoPlay) setVideoLoop(false)
    }, [ selectedVideo, videoAutoPlay ])

    useEffect(() => {
        if (videoLoop) setVideoAutoPlay(false)
    }, [ videoLoop ])

    const onClose = (e: any) => {
        e.preventDefault()
        setSelectedVideo(null)
        window.history.pushState(null, '', window.location.href.split('?')[0])
    }

    const startPrev = (e?: any) => {
        if (e) e.preventDefault()
        if (prevVideoRef.current) {
            setSelectedVideo(prevVideoRef.current)
            window.history.pushState(null, '', getVideoLink(prevVideoRef.current.hash))
        }
    }

    const startNext = (e?: any) => {
        if (e) e.preventDefault()
        if (nextVideoRef.current) {
            setSelectedVideo(nextVideoRef.current)
            window.history.pushState(null, '', getVideoLink(nextVideoRef.current.hash))
        }
    }

    const onVideoEnded = () => {
        if (videoAutoPlay || queue.length > 0) startNext()
    }

    const shuffleQueue = () => {
        if (queue.length === 0) return
        const current = queue[queueIndex]
        const rest = queue.filter((_, i) => i !== queueIndex).sort(() => Math.random() - 0.5)
        updateQueue([current, ...rest], 0)
    }

    useEffect(() => {
        const onFullscreenChange = () => {
            if (!document.fullscreenElement) {
                setFullScreen(false)
                window.scrollTo(0, scrollPosition)
            }
        }
        document.addEventListener('fullscreenchange', onFullscreenChange)
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
    }, [scrollPosition])

    const toggleFullScreen = (e?: any) => {
        if (e) e.preventDefault()
        if (!containerRef.current) return
        if (!document.fullscreenElement) {
            setScrollPosition(window.scrollY)
            containerRef.current.requestFullscreen()
            setFullScreen(true)
        } else {
            document.exitFullscreen()
        }
    }

    const onHoverStart = () => {
        setHasHover(true)
        if (lastHoverTimeout) window.clearTimeout(lastHoverTimeout)
        setLastHoverTimeout(window.setTimeout(() => setHasHover(false), 3000))
    }

    const onHoverEnd = () => {
        setHasHover(false)
        if (lastHoverTimeout) window.clearTimeout(lastHoverTimeout)
    }

    return (
        <>
            {selectedVideo && (
                <div
                    className={'video-layer' + (hasHover ? ' hover' : '')}
                    ref={onLayerOpen}
                    onMouseMove={onHoverStart}
                    onMouseLeave={onHoverEnd}
                >
                    {/* ── header ── */}
                    <header className="vl-header">
                        {selectedVideo.directory.hash ? (
                            <>
                                <Link
                                    className="vl-dir"
                                    to={`/dir/${selectedVideo.directory.hash}`}
                                    onClick={() => setSelectedVideo(null)}
                                >
                                    {selectedVideo.directory.name}
                                </Link>
                                <span className="vl-sep">/</span>
                            </>
                        ) : null}

                        <span className="vl-name">{selectedVideo.name}</span>

                        <div className="vl-tags">
                            <TagList video={selectedVideo} showAdd/>
                        </div>

                        <button
                            className="vl-open-folder"
                            onClick={() => api.openInFileManager(selectedVideo.hash)}
                            title="Open in file manager"
                        >
                            <FontAwesomeIcon icon={faFolderOpen}/>
                        </button>

                        <div className="vl-settings">
                            {selectedVideo.fileType === 'video' && (
                                <label>
                                    <input type="checkbox" checked={videoLoop}
                                           onChange={e => setVideoLoop(e.target.checked)}/>
                                    Loop
                                </label>
                            )}
                            <label>
                                <input type="checkbox" checked={videoAutoPlay}
                                       onChange={e => setVideoAutoPlay(e.target.checked)}
                                       disabled={selectedVideo.fileType === 'video' && videoLoop}/>
                                Auto
                            </label>
                        </div>

                        <a href="#" className="vl-close" onClick={onClose} title="Close (Esc)">✕</a>
                    </header>

                    {/* ── body ── */}
                    <div className="vl-body">
                        <div className="vl-main" ref={containerRef}>
                            {(selectedVideo.fileType === 'video' || selectedVideo.fileType === 'clip') && (
                                <VideoPlayer
                                    loop={videoLoop}
                                    autoPlay={videoAutoPlay}
                                    onEnded={onVideoEnded}
                                    fullScreen={fullScreen}
                                    enableFullscreen={toggleFullScreen}
                                    video={selectedVideo}
                                />
                            )}

                            {selectedVideo.fileType === 'photo' && (
                                <div className="vl-photo" onDoubleClick={toggleFullScreen}>
                                    <img src={selectedVideo.paths.stream} alt={selectedVideo.name}/>
                                </div>
                            )}

                            <nav className="vl-nav">
                                {prevVideo
                                    ? <a href="#" onClick={startPrev}>‹</a>
                                    : <div/>}
                                {nextVideo
                                    ? <a href="#" onClick={startNext}>›</a>
                                    : <div/>}
                            </nav>
                        </div>

                        {(queue.length > 0 || files.length > 0) && (
                            <div className="vl-sidebar">
                                {queue.length > 0 && (
                                    <div className="vl-sidebar-tabs">
                                        <button
                                            className={'vl-sidebar-tab' + (sidebarTab === 'queue' ? ' active' : '')}
                                            onClick={() => setSidebarTab('queue')}
                                        >Queue ({queue.length})</button>
                                        <button
                                            className={'vl-sidebar-tab' + (sidebarTab === 'files' ? ' active' : '')}
                                            onClick={() => setSidebarTab('files')}
                                        >Files</button>
                                    </div>
                                )}
                                {sidebarTab === 'queue' && queue.length > 0 ? (
                                    <QueuePanel
                                        queue={queue}
                                        currentIndex={queueIndex}
                                        onJump={(file, i) => {
                                            updateQueue(queue, i)
                                            setSelectedVideo(file)
                                        }}
                                        onShuffle={shuffleQueue}
                                        onClear={clearQueue}
                                    />
                                ) : (
                                    files.length > 0 && <VideoList files={files} loading={dirLoading} onLoad={onSmallListLoaded} showSort={false}/>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}
